import { memo, useCallback, useMemo, useState } from 'react';
import {
  Calendar,
  FileText,
  Folder,
  Info,
  LayoutGrid,
  List,
  Loader2,
  Lock,
  MoreVertical,
  Paperclip,
  Plus,
  User,
} from 'lucide-react';
import { Button, Dropdown, ModalLayout, SearchInput, StatusMessage, ViewToggle } from '@aimsos/ui';
import { useAgentKnowledgePage } from '../hooks/knowledge/useAgentKnowledgePage';
import { useAgentFileUpload } from '../hooks/knowledge/useAgentFileUpload';
import KnowledgeBrowseDrivesDrawer from './KnowledgeBrowseDrivesDrawer';

const viewToggleOptions = [
  { value: 'grid', icon: LayoutGrid, label: 'Grid view' },
  { value: 'list', icon: List, label: 'List view' },
];

function formatKnowledgeDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return String(value);
  }
}

function KnowledgeItemCard({
  item,
  kind,
  viewMode,
  onDriveRemove,
  onDocRemoveAccess,
  onDocDelete,
  removingDriveIds,
  removingDocumentIds,
  deletingDocumentIds,
  uploadEntry,
  t,
}) {
  const isDrive = kind === 'drive';
  const id = isDrive ? item.vaultUuid || item.uuid || item.id : item.uuid || item.id;
  const busy = isDrive
    ? removingDriveIds.has(id)
    : removingDocumentIds.has(id) || deletingDocumentIds.has(id);

  const isDocument = kind === 'vaultDoc' || kind === 'agentDoc';
  const isDocumentProcessing =
    isDocument &&
    (item.state === 'loading' ||
      (uploadEntry && !uploadEntry.error && uploadEntry.progress < 100));

  const title = item.name || id;
  const subtitle = isDrive
    ? item.description || t('edit.knowledge.card.noDescription')
    : item.type || '';

  const processingBadge = (
    <span
      className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-xs font-medium text-content-secondary dark:border-brand-800 dark:bg-brand-950/40"
      role="status"
      aria-live="polite"
    >
      <Info className="h-3.5 w-3.5 shrink-0 text-brand-600 dark:text-brand-400" aria-hidden />
      <span className="truncate">{t('edit.knowledge.upload.processing')}</span>
      <Loader2
        className="h-3.5 w-3.5 shrink-0 animate-spin text-brand-600 dark:text-brand-400"
        aria-hidden
      />
    </span>
  );

  const menuTrigger = (
    <button
      type="button"
      className="rounded-md p-1.5 text-content-muted hover:bg-surface-hover-subtle hover:text-content disabled:pointer-events-none disabled:opacity-40"
      aria-label={t('edit.knowledge.actions.menu')}
      disabled={busy || isDocumentProcessing}
    >
      <MoreVertical className="h-4 w-4" />
    </button>
  );

  const menu = (
    <Dropdown
      trigger={menuTrigger}
      usePortal
      alignMenu="right"
      menuPosition="bottom"
      disabled={busy || isDocumentProcessing}
    >
      {kind === 'drive' ? (
        <Dropdown.Item onClick={() => onDriveRemove(item)} disabled={busy}>
          {t('edit.knowledge.actions.removeDrive')}
        </Dropdown.Item>
      ) : null}
      {kind === 'agentDoc' ? (
        <Dropdown.Item onClick={() => onDocRemoveAccess(item)} disabled={busy}>
          {t('edit.knowledge.actions.removeAccess')}
        </Dropdown.Item>
      ) : null}
      {kind === 'vaultDoc' ? (
        <Dropdown.Item onClick={() => onDocDelete(item)} disabled={busy}>
          {t('edit.knowledge.actions.delete')}
        </Dropdown.Item>
      ) : null}
    </Dropdown>
  );

  const dateLabel = formatKnowledgeDate(
    item.updatedAt || item.updated_at || item.createdAt || item.created_at,
  );

  const lockStatusLabel = isDrive
    ? null
    : isDocumentProcessing
      ? t('edit.knowledge.upload.processingShort')
      : item.state || item.status || 'active';

  if (viewMode === 'list') {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-edge bg-surface px-3 py-2.5">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {isDrive ? (
            <Folder className="h-4 w-4 shrink-0 text-content-muted" />
          ) : (
            <FileText className="h-4 w-4 shrink-0 text-content-muted" />
          )}
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-content">{title}</p>
            {subtitle ? (
              <p className="truncate text-xs text-content-muted">{subtitle}</p>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isDocumentProcessing ? processingBadge : null}
          {menu}
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col rounded-xl border border-edge bg-surface p-4 shadow-sm">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          {isDrive ? (
            <Folder className="h-5 w-5 shrink-0 text-content-muted" />
          ) : (
            <FileText className="h-5 w-5 shrink-0 text-content-muted" />
          )}
          <div className="min-w-0">
            <p className="line-clamp-2 text-sm font-semibold text-content">{title}</p>
            {subtitle ? <p className="mt-1 line-clamp-2 text-xs text-content-muted">{subtitle}</p> : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {isDocumentProcessing ? processingBadge : null}
          {menu}
        </div>
      </div>
      <div className="mt-auto flex flex-wrap gap-2 border-t border-edge-subtle pt-3 text-xs text-content-muted">
        <span className="inline-flex items-center gap-1">
          <User className="h-3.5 w-3.5" />
          {item.owner || item.createdBy || t('edit.knowledge.card.unknown')}
        </span>
        {isDrive ? (
          <span className="inline-flex items-center gap-1">
            <Paperclip className="h-3.5 w-3.5" />
            {item.fileCount ?? item.documentCount ?? 0} {t('edit.knowledge.card.files')}
          </span>
        ) : (
          <span
            className={[
              'inline-flex items-center gap-1',
              isDocumentProcessing
                ? 'rounded-md border border-brand-200 bg-brand-50/60 px-1.5 py-0.5 dark:border-brand-800 dark:bg-brand-950/30'
                : '',
            ].join(' ')}
          >
            <Lock
              className={[
                'h-3.5 w-3.5',
                isDocumentProcessing ? 'text-brand-600 dark:text-brand-400' : 'text-emerald-600',
              ].join(' ')}
            />
            {lockStatusLabel}
          </span>
        )}
        <span className="inline-flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5" />
          {dateLabel}
        </span>
      </div>
    </div>
  );
}

const KnowledgeTabPanel = memo(({ t, toast, disabled }) => {
  const k = useAgentKnowledgePage({ t, toast });
  const {
    fileInputRef,
    uploadingFiles,
    handleFileSelect,
    uploadErrors,
    clearErrors,
    openFilePicker,
  } = useAgentFileUpload({
    onUploadStart: () => k.handleTabChange(1),
  });

  const [showBrowse, setShowBrowse] = useState(false);

  const uploadBanner = useMemo(() => {
    if (uploadErrors.length === 0) return '';
    if (uploadErrors.length === 1) {
      return `${uploadErrors[0].fileName}: ${uploadErrors[0].message}`;
    }
    return t('edit.knowledge.upload.multiError', { count: uploadErrors.length });
  }, [uploadErrors, t]);

  const handleAddMenu = useCallback(
    (action) => {
      if (action === 'browse') setShowBrowse(true);
      if (action === 'upload') openFilePicker();
    },
    [openFilePicker],
  );

  if (disabled) {
    return (
      <StatusMessage
        icon={Info}
        title={t('edit.knowledge.needAgentTitle')}
        subtitle={t('edit.knowledge.needAgentSubtitle')}
      />
    );
  }

  const tab0 = k.activeTab === 0;
  const deleteDocumentTargetId = k.itemToRemove?.uuid ?? k.itemToRemove?.id ?? null;
  const isDeleteDocumentSubmitting = Boolean(
    deleteDocumentTargetId && k.deletingDocumentIds.has(deleteDocumentTargetId),
  );
  const deleteDocumentFileName = k.itemToRemove?.name || deleteDocumentTargetId || '';

  return (
    <div className="flex flex-col gap-4">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-content">{t('edit.knowledge.dataAccessTitle')}</h2>
          <p className="mt-1 text-sm text-content-muted">{t('edit.knowledge.dataAccessSubtitle')}</p>
        </div>
        <Dropdown
          label={t('edit.knowledge.add')}
          variant="secondary"
          size="medium"
          triggerIcon={Plus}
          iconPosition="left"
          // The tab body is a `w-1/2 overflow-y-auto` column, and `overflow-y: auto` makes the
          // browser compute `overflow-x: auto` too — so an inline menu is clipped at the column
          // edge and, since the panel stays mounted while closed, permanently widens the column's
          // scrollWidth and leaves a horizontal scrollbar under it. Portalling takes the menu out
          // of that box entirely; right alignment keeps it under a trigger that sits at the column
          // edge. Same pair the row menu in this file already uses. (CT-5300)
          usePortal
          alignMenu="right"
        >
          <Dropdown.Item onClick={() => handleAddMenu('browse')}>
            {t('edit.knowledge.addBrowse')}
          </Dropdown.Item>
          <Dropdown.Item onClick={() => handleAddMenu('upload')}>{t('edit.knowledge.addUpload')}</Dropdown.Item>
        </Dropdown>
      </div>

      {uploadBanner ? (
        <div
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
          role="alert"
        >
          <div className="flex justify-between gap-2">
            <span className="whitespace-pre-wrap">{uploadBanner}</span>
            <button
              type="button"
              className="shrink-0 text-red-700 underline dark:text-red-300"
              onClick={() => clearErrors()}
            >
              {t('edit.knowledge.dismiss')}
            </button>
          </div>
        </div>
      ) : null}

      <SearchInput
        value={k.searchTerm}
        onChange={(e) => k.handleSearchChange(e.target.value)}
        placeholder={t('edit.knowledge.searchPlaceholder')}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => k.handleTabChange(0)}
            className={[
              'rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
              tab0 ? 'bg-brand-500 text-white' : 'border border-edge bg-surface text-content',
            ].join(' ')}
          >
            {t('edit.knowledge.tabDrives')}
          </button>
          <button
            type="button"
            onClick={() => k.handleTabChange(1)}
            className={[
              'rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
              !tab0 ? 'bg-brand-500 text-white' : 'border border-edge bg-surface text-content',
            ].join(' ')}
          >
            {t('edit.knowledge.tabDocuments')}
          </button>
        </div>
        <ViewToggle view={k.viewMode} onChange={k.handleViewModeChange} options={viewToggleOptions} />
      </div>

      {k.loading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-content-muted">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">{t('edit.knowledge.loading')}</p>
        </div>
      ) : k.error ? (
        <StatusMessage
          icon={Info}
          title={t('edit.knowledge.loadError')}
          subtitle={k.error?.message ?? t('edit.knowledge.loadErrorHint')}
        />
      ) : k.totalItems === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
          <p className="text-sm font-medium text-content-secondary">{t('edit.knowledge.emptyTitle')}</p>
          <p className="max-w-md text-xs text-content-muted">{t('edit.knowledge.emptyHint')}</p>
        </div>
      ) : k.viewMode === 'grid' ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {tab0
            ? k.drives.map((drive) => (
                <KnowledgeItemCard
                  key={drive.vaultUuid || drive.uuid || drive.id}
                  item={drive}
                  kind="drive"
                  viewMode="grid"
                  onDriveRemove={k.handleRemoveDriveAccess}
                  onDocRemoveAccess={k.handleDeleteAgentDocument}
                  onDocDelete={k.handleDeleteVaultDocument}
                  removingDriveIds={k.removingDriveIds}
                  removingDocumentIds={k.removingDocumentIds}
                  deletingDocumentIds={k.deletingDocumentIds}
                  t={t}
                />
              ))
            : null}
          {tab0
            ? k.agentDocuments.map((doc) => (
                <KnowledgeItemCard
                  key={doc.uuid || doc.id}
                  item={doc}
                  kind="agentDoc"
                  viewMode="grid"
                  onDriveRemove={k.handleRemoveDriveAccess}
                  onDocRemoveAccess={k.handleDeleteAgentDocument}
                  onDocDelete={k.handleDeleteVaultDocument}
                  removingDriveIds={k.removingDriveIds}
                  removingDocumentIds={k.removingDocumentIds}
                  deletingDocumentIds={k.deletingDocumentIds}
                  uploadEntry={uploadingFiles[doc.uuid || doc.id]}
                  t={t}
                />
              ))
            : null}
          {!tab0
            ? k.vaultDocuments.map((doc) => (
                <KnowledgeItemCard
                  key={doc.uuid || doc.id}
                  item={doc}
                  kind="vaultDoc"
                  viewMode="grid"
                  onDriveRemove={k.handleRemoveDriveAccess}
                  onDocRemoveAccess={k.handleDeleteAgentDocument}
                  onDocDelete={k.handleDeleteVaultDocument}
                  removingDriveIds={k.removingDriveIds}
                  removingDocumentIds={k.removingDocumentIds}
                  deletingDocumentIds={k.deletingDocumentIds}
                  uploadEntry={uploadingFiles[doc.uuid || doc.id]}
                  t={t}
                />
              ))
            : null}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {tab0
            ? k.drives.map((drive) => (
                <KnowledgeItemCard
                  key={drive.vaultUuid || drive.uuid || drive.id}
                  item={drive}
                  kind="drive"
                  viewMode="list"
                  onDriveRemove={k.handleRemoveDriveAccess}
                  onDocRemoveAccess={k.handleDeleteAgentDocument}
                  onDocDelete={k.handleDeleteVaultDocument}
                  removingDriveIds={k.removingDriveIds}
                  removingDocumentIds={k.removingDocumentIds}
                  deletingDocumentIds={k.deletingDocumentIds}
                  t={t}
                />
              ))
            : null}
          {tab0
            ? k.agentDocuments.map((doc) => (
                <KnowledgeItemCard
                  key={doc.uuid || doc.id}
                  item={doc}
                  kind="agentDoc"
                  viewMode="list"
                  onDriveRemove={k.handleRemoveDriveAccess}
                  onDocRemoveAccess={k.handleDeleteAgentDocument}
                  onDocDelete={k.handleDeleteVaultDocument}
                  removingDriveIds={k.removingDriveIds}
                  removingDocumentIds={k.removingDocumentIds}
                  deletingDocumentIds={k.deletingDocumentIds}
                  uploadEntry={uploadingFiles[doc.uuid || doc.id]}
                  t={t}
                />
              ))
            : null}
          {!tab0
            ? k.vaultDocuments.map((doc) => (
                <KnowledgeItemCard
                  key={doc.uuid || doc.id}
                  item={doc}
                  kind="vaultDoc"
                  viewMode="list"
                  onDriveRemove={k.handleRemoveDriveAccess}
                  onDocRemoveAccess={k.handleDeleteAgentDocument}
                  onDocDelete={k.handleDeleteVaultDocument}
                  removingDriveIds={k.removingDriveIds}
                  removingDocumentIds={k.removingDocumentIds}
                  deletingDocumentIds={k.deletingDocumentIds}
                  uploadEntry={uploadingFiles[doc.uuid || doc.id]}
                  t={t}
                />
              ))
            : null}
        </div>
      )}

      <KnowledgeBrowseDrivesDrawer
        isOpen={showBrowse}
        onClose={() => setShowBrowse(false)}
        t={t}
        toast={toast}
      />

      <ModalLayout
        isOpen={k.showRemoveDriveModal}
        onClose={k.handleCloseRemoveDriveModal}
        title={t('edit.knowledge.modal.removeDriveTitle')}
        subtitle={t('edit.knowledge.modal.removeDriveBody')}
        maxWidthClassName="max-w-md"
        heightClassName="h-auto"
        useBodyPortal
      >
        <div className="flex justify-end gap-2 border-t border-edge px-6 py-4">
          <Button type="button" variant="secondary" size="sm" onClick={k.handleCloseRemoveDriveModal}>
            {t('edit.knowledge.modal.cancel')}
          </Button>
          <Button type="button" variant="primary" size="sm" onClick={() => void k.handleConfirmRemoveDrive()}>
            {t('edit.knowledge.modal.confirm')}
          </Button>
        </div>
      </ModalLayout>

      <ModalLayout
        isOpen={k.showRemoveDocumentModal}
        onClose={k.handleCloseRemoveDocumentModal}
        title={t('edit.knowledge.modal.removeDocTitle')}
        subtitle={t('edit.knowledge.modal.removeDocBody')}
        maxWidthClassName="max-w-md"
        heightClassName="h-auto"
        useBodyPortal
      >
        <div className="flex justify-end gap-2 border-t border-edge px-6 py-4">
          <Button type="button" variant="secondary" size="sm" onClick={k.handleCloseRemoveDocumentModal}>
            {t('edit.knowledge.modal.cancel')}
          </Button>
          <Button type="button" variant="primary" size="sm" onClick={() => void k.handleConfirmRemoveDocument()}>
            {t('edit.knowledge.modal.confirm')}
          </Button>
        </div>
      </ModalLayout>

      <ModalLayout
        isOpen={k.showDeleteDocumentModal}
        onClose={k.handleCloseDeleteDocumentModal}
        closeDisabled={isDeleteDocumentSubmitting}
        title={t('edit.knowledge.modal.deleteDocTitle')}
        subtitle={t('edit.knowledge.modal.deleteDocBody')}
        subtitleClassName="mt-3 text-left text-sm leading-relaxed text-content-secondary"
        maxWidthClassName="max-w-md"
        heightClassName="h-auto"
        useBodyPortal
      >
        <div className="px-5 pb-5">
          <div className="rounded-lg bg-surface-secondary px-4 py-3 text-center text-sm text-content-secondary">
            <span className="break-words" title={deleteDocumentFileName}>
              {deleteDocumentFileName}
            </span>
          </div>
        </div>
        <div className="flex justify-center gap-3 border-t border-edge px-6 py-4">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={isDeleteDocumentSubmitting}
            onClick={k.handleCloseDeleteDocumentModal}
          >
            {t('edit.knowledge.modal.cancel')}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            loading={isDeleteDocumentSubmitting}
            onClick={() => void k.handleConfirmDeleteDocument()}
          >
            {t('edit.knowledge.modal.delete')}
          </Button>
        </div>
      </ModalLayout>
    </div>
  );
});

KnowledgeTabPanel.displayName = 'KnowledgeTabPanel';

export default KnowledgeTabPanel;
